import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { useState, useEffect } from "react";
import { getApiUrl } from "./utils/api";

// Pages
import LandingPage from "./pages/LandingPage";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import Onboarding from "./pages/Onboarding";
import SiteEditor from "./pages/SiteEditor";
import GiftManager from "./pages/GiftManager";
import RSVPManager from "./pages/RSVPManager";
import PublicWeddingSite from "./pages/PublicWeddingSite";
import CheckoutPage from "./pages/CheckoutPage";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import AdminPanel from "./pages/AdminPanel";
import BankSettings from "./pages/BankSettings";
import GuestListManager from "./pages/GuestListManager";
import MessagesWall from "./pages/MessagesWall";
import NotificationSettings from "./pages/NotificationSettings";
import EventInfoEditor from "./pages/EventInfoEditor";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const location = useLocation();

  useEffect(() => {
    // If user data passed from AuthCallback, skip auth check
    if (location.state?.user) {
      setIsAuthenticated(true);
      setUser(location.state.user);
      return;
    }

    const checkAuth = async () => {
      try {
        const API = getApiUrl();
        const response = await fetch(`${API}/auth/me`, {
          credentials: 'include'
        });
        if (!response.ok) throw new Error('Not authenticated');
        const userData = await response.json();
        setIsAuthenticated(true);
        setUser(userData);
      } catch (error) {
        setIsAuthenticated(false);
      }
    };
    checkAuth();
  }, [location.state]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="animate-pulse text-[#6E0F1A] font-serif text-xl">Carregando...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// App Router with session_id detection
const AppRouter = () => {
  const location = useLocation();

  // Detect session_id in URL fragment SYNCHRONOUSLY during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute><Dashboard /></ProtectedRoute>
      } />
      <Route path="/onboarding" element={
        <ProtectedRoute><Onboarding /></ProtectedRoute>
      } />
      <Route path="/editor" element={
        <ProtectedRoute><SiteEditor /></ProtectedRoute>
      } />
      <Route path="/gifts" element={
        <ProtectedRoute><GiftManager /></ProtectedRoute>
      } />
      <Route path="/rsvps" element={
        <ProtectedRoute><RSVPManager /></ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute><AdminPanel /></ProtectedRoute>
      } />
      <Route path="/bank-settings" element={
        <ProtectedRoute><BankSettings /></ProtectedRoute>
      } />
      <Route path="/guest-list" element={
        <ProtectedRoute><GuestListManager /></ProtectedRoute>
      } />
      <Route path="/messages" element={
        <ProtectedRoute><MessagesWall /></ProtectedRoute>
      } />
      <Route path="/notifications" element={
        <ProtectedRoute><NotificationSettings /></ProtectedRoute>
      } />
      <Route path="/event-info" element={
        <ProtectedRoute><EventInfoEditor /></ProtectedRoute>
      } />
      <Route path="/casamento/:slug" element={<PublicWeddingSite />} />
      <Route path="/evento/:slug" element={<PublicWeddingSite />} />
      <Route path="/checkout/:siteId" element={<CheckoutPage />} />
      <Route path="/checkout/success" element={<CheckoutSuccess />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AppRouter />
      <Toaster position="top-right" richColors />
    </BrowserRouter>
  );
}

export default App;
